// @ts-strict-ignore
import { TopNav } from "@dashboard/components/AppLayout/TopNav";
import { DashboardCard } from "@dashboard/components/Card";
import CardSpacer from "@dashboard/components/CardSpacer";
import { ConfirmButtonTransitionState } from "@dashboard/components/ConfirmButton";
import Form from "@dashboard/components/Form";
import { DetailPageLayout } from "@dashboard/components/Layouts";
import ResponsiveTable from "@dashboard/components/ResponsiveTable";
import { Savebar } from "@dashboard/components/Savebar";
import TableRowLink from "@dashboard/components/TableRowLink";
import {
  FulfillOrderMutation,
  OrderErrorCode,
  OrderFulfillDataQuery,
  OrderFulfillLineFragment,
  OrderFulfillStockInput,
  ShopOrderSettingsFragment,
} from "@dashboard/graphql";
import { SubmitPromise } from "@dashboard/hooks/useForm";
import useFormset, { FormsetData } from "@dashboard/hooks/useFormset";
import useNavigator from "@dashboard/hooks/useNavigator";
import { commonMessages } from "@dashboard/intl";
import { renderCollection } from "@dashboard/misc";
import OrderChangeWarehouseDialog from "@dashboard/orders/components/OrderChangeWarehouseDialog";
import {
  OrderFulfillUrlDialog,
  OrderFulfillUrlQueryParams,
  orderUrl,
} from "@dashboard/orders/urls";
import {
  getAttributesCaption,
  getLineAllocationWithHighestQuantity,
  getToFulfillOrderLines,
  getWarehousesFromOrderLines,
  OrderFulfillLineFormData,
} from "@dashboard/orders/utils/data";
import {
  getRecordedPontoRecolhaAddress,
  isParcelShopDelivery,
  isStorePickup,
} from "@dashboard/orders/utils/delivery";
import {
  PONTO_RECOLHA_ADDRESS_MAX_LENGTH,
  pontoRecolhaMessages,
} from "@dashboard/orders/utils/pontoRecolha";
import useWarehouseSearch from "@dashboard/searches/useWarehouseSearch";
import getOrderErrorMessage from "@dashboard/utils/errors/order";
import { mapEdgesToItems } from "@dashboard/utils/maps";
import { TableBody, TableCell, TableHead } from "@material-ui/core";
import { Box, Checkbox, Input, Skeleton, Text, Textarea, Tooltip } from "@saleor/macaw-ui-next";
import clsx from "clsx";
import { useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import OrderFulfillLine from "../OrderFulfillLine/OrderFulfillLine";
import OrderFulfillStockExceededDialog from "../OrderFulfillStockExceededDialog";
import { messages } from "./messages";
import moduleClasses from "./OrderFulfillPage.module.css";
import { useStyles } from "./styles";

// Name of the warehouse created by Saleor's default setup.
const DEFAULT_WAREHOUSE_NAME = "default warehouse";

interface OrderFulfillFormData {
  sendInfo: boolean;
  allowStockToBeExceeded: boolean;
  trackingNumber: string | undefined;
  pontoRecolhaAddress: string;
  // In-store pickup: also mark the order ready for pickup, and optionally email
  // the customer about it.
  markReadyForPickup: boolean;
  notifyReadyForPickup: boolean;
}
export interface OrderFulfillSubmitData extends OrderFulfillFormData {
  items: FormsetData<null, OrderFulfillStockInput[]>;
}
interface OrderFulfillPageProps {
  params: OrderFulfillUrlQueryParams;
  loading: boolean;
  errors: FulfillOrderMutation["orderFulfill"]["errors"];
  order: OrderFulfillDataQuery["order"];
  saveButtonBar: ConfirmButtonTransitionState;
  shopSettings?: ShopOrderSettingsFragment;
  onSubmit: (data: OrderFulfillSubmitData) => SubmitPromise;
  openModal: (action: OrderFulfillUrlDialog, params?: OrderFulfillUrlQueryParams) => void;
  closeModal: () => void;
}

const getInitialFormData = (order: OrderFulfillDataQuery["order"]): OrderFulfillFormData => ({
  // In-store pickup ships nothing, so the "your order has shipped" email would be
  // wrong; the customer is told via "Mark ready for pickup" instead.
  sendInfo: !isStorePickup(order?.deliveryType),
  allowStockToBeExceeded: false,
  trackingNumber: undefined,
  pontoRecolhaAddress: getRecordedPontoRecolhaAddress(order?.metadata),
  markReadyForPickup: true,
  notifyReadyForPickup: true,
});
const OrderFulfillPage = (props: OrderFulfillPageProps) => {
  const {
    params,
    loading,
    errors,
    order,
    saveButtonBar,
    shopSettings,
    onSubmit,
    openModal,
    closeModal,
  } = props;
  const intl = useIntl();
  const classes = useStyles(props);
  const navigate = useNavigator();
  // When the order isn't stocked in more than one warehouse, default the
  // fulfillment to that single warehouse so it doesn't have to be picked manually.
  const orderWarehouses = getWarehousesFromOrderLines(order?.lines);
  const onlyWarehouse = orderWarehouses?.length === 1 ? orderWarehouses[0] : undefined;
  // Fallback default for lines with no allocation and no stock record (e.g. 0
  // available, so the warehouse select has nothing to suggest): pre-select the
  // shop's default warehouse — the sole warehouse, else the one with the
  // "Default Warehouse" name, else the first one — so it never needs a manual pick.
  const { result: warehousesOpts } = useWarehouseSearch({
    variables: { after: null, channnelsId: null, first: 20, query: "" },
  });
  const shopWarehouses = mapEdgesToItems(warehousesOpts?.data?.search) ?? [];
  const soleWarehouse =
    shopWarehouses.find(
      warehouse => warehouse.name.trim().toLowerCase() === DEFAULT_WAREHOUSE_NAME,
    ) ?? shopWarehouses[0];
  const { change: formsetChange, data: formsetData } = useFormset<null, OrderFulfillLineFormData[]>(
    (getToFulfillOrderLines(order?.lines) as OrderFulfillLineFragment[]).map(line => {
      const highestQuantityAllocation = getLineAllocationWithHighestQuantity(line);

      return {
        data: null,
        id: line.id,
        label: getAttributesCaption(line?.variant?.attributes),
        value: line?.variant?.preorder
          ? null
          : [
              {
                quantity: line.quantityToFulfill,
                warehouse: highestQuantityAllocation?.warehouse ?? onlyWarehouse,
              },
            ],
      };
    }),
  );
  const [displayStockExceededDialog, setDisplayStockExceededDialog] = useState(false);
  const pontoRecolhaError = errors?.find(error => error.field === "pontoRecolhaAddress");
  const handleSubmit = ({
    formData,
    allowStockToBeExceeded,
  }: {
    formData: OrderFulfillFormData;
    allowStockToBeExceeded: boolean;
  }) => {
    setDisplayStockExceededDialog(false);

    return onSubmit({
      ...formData,
      allowStockToBeExceeded,
      items: formsetData
        .filter(item => !!item.value)
        .map(item => ({
          ...item,
          value: item.value.map(value => ({
            quantity: value.quantity,
            warehouse: value.warehouse.id,
          })),
        })),
    });
  };

  useEffect(() => {
    if (errors && errors.every(err => err.code === OrderErrorCode.INSUFFICIENT_STOCK)) {
      setDisplayStockExceededDialog(true);
    }
  }, [errors]);

  // The sole-warehouse list resolves asynchronously, after the formset is
  // initialised, so backfill any line that still has no warehouse once it loads.
  // Staff can still override per line via the "Change warehouse" dialog.
  useEffect(() => {
    if (!soleWarehouse) {
      return;
    }

    formsetData.forEach(line => {
      if (line.value?.some(entry => !entry.warehouse)) {
        formsetChange(
          line.id,
          line.value.map(entry =>
            entry.warehouse ? entry : { ...entry, warehouse: soleWarehouse },
          ),
        );
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [soleWarehouse?.id]);

  const notAllowedToFulfillUnpaid =
    shopSettings?.fulfillmentAutoApprove && !shopSettings?.fulfillmentAllowUnpaid && !order?.isPaid;
  const areWarehousesSet = formsetData
    .filter(item => !!item?.value) // preorder case
    .every(line => line.value.every(v => v.warehouse));
  const isParcelShop = isParcelShopDelivery(order?.deliveryType);
  // In-store pickup ships nothing: the page is about preparing the order, with no
  // tracking number or "shipped" email.
  const isPickup = isStorePickup(order?.deliveryType);
  const shouldEnableSave = (data: OrderFulfillFormData) => {
    if (!order || loading) {
      return false;
    }

    // The shipping email must name the pickup point the parcel goes to.
    if (isParcelShop && !data.pontoRecolhaAddress.trim()) {
      return false;
    }

    if (notAllowedToFulfillUnpaid) {
      return false;
    }

    const isAtLeastOneFulfilled = formsetData?.some(el => el.value?.[0]?.quantity > 0);
    const overfulfill = formsetData
      .filter(item => !!item?.value) // this can be removed after preorder is dropped
      .some(item => {
        const formQuantityFulfilled = item?.value?.[0]?.quantity;
        const quantityToFulfill = order?.lines?.find(line => line.id === item.id).quantityToFulfill;

        return formQuantityFulfilled > quantityToFulfill;
      });

    return !overfulfill && isAtLeastOneFulfilled && areWarehousesSet;
  };

  return (
    <DetailPageLayout gridTemplateColumns={1}>
      <TopNav
        href={orderUrl(order?.id)}
        title={intl.formatMessage(
          isPickup
            ? messages.headerOrderNumberPrepareForPickup
            : messages.headerOrderNumberAddFulfillment,
          { orderNumber: order?.number },
        )}
      />
      <DetailPageLayout.Content>
        <Form
          confirmLeave
          initial={getInitialFormData(order)}
          onSubmit={formData =>
            handleSubmit({
              formData,
              allowStockToBeExceeded: displayStockExceededDialog,
            })
          }
        >
          {({ change, data, submit }) => (
            <>
              <DashboardCard>
                <DashboardCard.Header>
                  <DashboardCard.Title>
                    {intl.formatMessage(
                      isPickup ? messages.itemsToPrepare : messages.itemsReadyToShip,
                    )}
                  </DashboardCard.Title>
                </DashboardCard.Header>
                {order ? (
                  <ResponsiveTable className={clsx(classes.table, moduleClasses.stackedTable)}>
                    <TableHead>
                      <TableRowLink>
                        <TableCell className={classes.colName}>
                          <FormattedMessage {...messages.productName} />
                        </TableCell>
                        <TableCell className={classes.colSku}>
                          <FormattedMessage {...messages.sku} />
                        </TableCell>
                        <TableCell className={clsx(classes.colQuantity, classes.colQuantityHeader)}>
                          <FormattedMessage {...messages.quantity} />
                        </TableCell>
                        <TableCell className={classes.colStock}>
                          <FormattedMessage {...messages.stock} />
                        </TableCell>
                        <TableCell className={classes.colWarehouse}>
                          <FormattedMessage {...messages.warehouse} />
                        </TableCell>
                      </TableRowLink>
                    </TableHead>
                    <TableBody>
                      {renderCollection(
                        getToFulfillOrderLines(order.lines),
                        (line: OrderFulfillLineFragment, lineIndex) => (
                          <OrderFulfillLine
                            key={line.id}
                            line={line}
                            lineIndex={lineIndex}
                            formsetData={formsetData}
                            formsetChange={formsetChange}
                            onWarehouseChange={() =>
                              openModal("change-warehouse", {
                                lineId: line.id,
                                warehouseId: formsetData[lineIndex]?.value?.[0]?.warehouse?.id,
                              })
                            }
                          />
                        ),
                      )}
                    </TableBody>
                  </ResponsiveTable>
                ) : (
                  <DashboardCard.Content>
                    <Skeleton />
                  </DashboardCard.Content>
                )}
              </DashboardCard>

              <CardSpacer />

              {isParcelShop && (
                <>
                  <DashboardCard>
                    <DashboardCard.Header>
                      <DashboardCard.Title>
                        {intl.formatMessage(pontoRecolhaMessages.cardTitle)}
                      </DashboardCard.Title>
                    </DashboardCard.Header>
                    <DashboardCard.Content display="grid" gap={2}>
                      <Textarea
                        name="pontoRecolhaAddress"
                        label={intl.formatMessage(pontoRecolhaMessages.addressLabel)}
                        value={data.pontoRecolhaAddress}
                        onChange={change}
                        rows={3}
                        maxLength={PONTO_RECOLHA_ADDRESS_MAX_LENGTH}
                        error={!!pontoRecolhaError}
                        helperText={
                          pontoRecolhaError
                            ? getOrderErrorMessage(pontoRecolhaError, intl)
                            : intl.formatMessage(pontoRecolhaMessages.addressHelperText)
                        }
                        data-test-id="ponto-recolha-address-input"
                      />
                    </DashboardCard.Content>
                  </DashboardCard>
                  <CardSpacer />
                </>
              )}

              {isPickup && (
                <DashboardCard>
                  <DashboardCard.Header>
                    <DashboardCard.Title>
                      {intl.formatMessage(messages.storePickupTitle)}
                    </DashboardCard.Title>
                  </DashboardCard.Header>
                  <DashboardCard.Content display="grid" gap={3}>
                    <Text>{intl.formatMessage(pontoRecolhaMessages.storePickupNotice)}</Text>
                    <Checkbox
                      checked={data.markReadyForPickup}
                      name="markReadyForPickup"
                      onCheckedChange={checked =>
                        change({ target: { name: "markReadyForPickup", value: checked } })
                      }
                      data-test-id="mark-ready-for-pickup"
                    >
                      <Text>{intl.formatMessage(messages.markReadyForPickup)}</Text>
                    </Checkbox>
                    <Box paddingLeft={6}>
                      <Checkbox
                        checked={data.markReadyForPickup && data.notifyReadyForPickup}
                        disabled={!data.markReadyForPickup}
                        name="notifyReadyForPickup"
                        onCheckedChange={checked =>
                          change({ target: { name: "notifyReadyForPickup", value: checked } })
                        }
                        data-test-id="notify-ready-for-pickup"
                      >
                        <Text color={data.markReadyForPickup ? "default1" : "default2"}>
                          {intl.formatMessage(messages.notifyReadyForPickup)}
                        </Text>
                      </Checkbox>
                    </Box>
                  </DashboardCard.Content>
                </DashboardCard>
              )}

              {shopSettings?.fulfillmentAutoApprove && !isPickup && (
                <DashboardCard>
                  <DashboardCard.Header>
                    <DashboardCard.Title>
                      {intl.formatMessage(messages.shipmentInformation)}
                    </DashboardCard.Title>
                  </DashboardCard.Header>
                  <DashboardCard.Content __maxWidth="fit-content" display="grid" gap={4}>
                    <Input
                      name="trackingNumber"
                      label={intl.formatMessage(messages.trackingNumberInputLabel)}
                      value={data.trackingNumber}
                      onChange={change}
                      helperText={intl.formatMessage(messages.trackingNumberInputHelperText)}
                    />
                    <Checkbox
                      checked={data.sendInfo}
                      name="sendInfo"
                      onCheckedChange={checked =>
                        change({ target: { name: "sendInfo", value: checked } })
                      }
                    >
                      <Text>{intl.formatMessage(messages.sentFulfillmentDetails)}</Text>
                    </Checkbox>
                  </DashboardCard.Content>
                </DashboardCard>
              )}

              <Savebar>
                <Savebar.Spacer />
                <Savebar.CancelButton onClick={() => navigate(orderUrl(order?.id))} />
                <Tooltip>
                  <Tooltip.Trigger>
                    <Box>
                      <Savebar.ConfirmButton
                        transitionState={saveButtonBar}
                        onClick={submit}
                        disabled={!shouldEnableSave(data)}
                      >
                        {isPickup
                          ? intl.formatMessage(messages.submitPreparedForPickup)
                          : shopSettings?.fulfillmentAutoApprove
                            ? intl.formatMessage(messages.submitFulfillment)
                            : intl.formatMessage(messages.submitPrepareFulfillment)}
                      </Savebar.ConfirmButton>
                    </Box>
                  </Tooltip.Trigger>
                  <Tooltip.Content>
                    {notAllowedToFulfillUnpaid &&
                      intl.formatMessage(commonMessages.cannotFullfillUnpaidOrder)}
                  </Tooltip.Content>
                </Tooltip>
              </Savebar>
              <OrderFulfillStockExceededDialog
                open={displayStockExceededDialog}
                lines={order?.lines}
                formsetData={formsetData}
                confirmButtonState={saveButtonBar}
                onSubmit={submit}
                onClose={() => setDisplayStockExceededDialog(false)}
              />
            </>
          )}
        </Form>
        <OrderChangeWarehouseDialog
          open={params.action === "change-warehouse"}
          line={order?.lines.find(line => line.id === params.lineId)}
          currentWarehouseId={params.warehouseId}
          onConfirm={warehouse => {
            const lineFormQuantity = formsetData.find(item => item.id === params.lineId)?.value?.[0]
              ?.quantity;

            formsetChange(params.lineId, [
              {
                quantity: lineFormQuantity,
                warehouse,
              },
            ]);
          }}
          onClose={closeModal}
        />
      </DetailPageLayout.Content>
    </DetailPageLayout>
  );
};

OrderFulfillPage.displayName = "OrderFulfillPage";
export default OrderFulfillPage;
